import { useQueryClient } from '@tanstack/react-query';
import Nango from '@nangohq/frontend';
import { toast } from 'sonner';
import { useCreateConnectSession } from './useIntegrations';

interface UseNangoConnectOptions {
  onSuccess?: (payload: unknown) => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
  successMessage?: string;
}

export const useNangoConnect = ({
  onSuccess,
  onError,
  onOpen,
  successMessage = 'Successfully connected!',
}: UseNangoConnectOptions) => {
  const queryClient = useQueryClient();
  const createSessionMutation = useCreateConnectSession();

  const connect = async (
    options: {
      crmOrgUrl?: string;
      providerConfigKey?: string;
    } = {}
  ) => {
    try {
      const { sessionToken } = await createSessionMutation.mutateAsync(options);

      const nango = new Nango({
        host: import.meta.env.VITE_NANGO_BASE_URL,
        connectSessionToken: sessionToken,
      });

      onOpen?.();

      const connectUI = nango.openConnectUI({
        onEvent: (event) => {
          if (event.type === 'close') {
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
          } else if (event.type === 'connect') {
            toast.success(successMessage);
            queryClient.invalidateQueries({ queryKey: ['integrations'] });

            // TODO: Replace setTimeout with polling or server-sent events
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['integrations'] });
            }, 3000);

            onSuccess?.(event.payload);
          }
        },
      });

      connectUI.setSessionToken(sessionToken);
    } catch (error) {
      toast.error('Failed to connect integration');
      onError?.(error as Error);
    }
  };

  return {
    connect,
    isConnecting: createSessionMutation.isPending,
  };
};
